import { useState } from "react";
import { Breadcrumb, Button } from "flowbite-react";
import { HiHome, HiPlus } from "react-icons/hi";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useTranslation } from "react-i18next";
import { useGetLocationAliasesQuery, useCreateLocationAliasMutation } from "@/api/locationApi/locationAlias";
import { getErrorMessage } from "@/utils/requestUtils";
import { toast } from "@/lib/toast";
import LocationAliasListTable from "./LocationAliasListTable";
import LocationAliasFormDialog from "./LocationAliasFormDialog";
import { LocationAlias, QueryParams } from "@/sharedTypes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

export default function LocationAliasList() {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const user = useCurrentUser();

  const [queryParams, setQueryParams] = useState<QueryParams>({
    filters: {},
    pagination: { page: 1, pageSize: 15 },
  });

  const { data: aliasesData } = useGetLocationAliasesQuery(queryParams);
  const [createLocationAlias] = useCreateLocationAliasMutation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleCreate = (data: LocationAlias) => {
    createLocationAlias(data)
      .unwrap()
      .then(() => {
        toast.success(t("temporalActivity:locationAlias.createdSuccess"));
        setIsCreateModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handlePageChange = (page: number) => {
    setQueryParams((prev) => ({ ...prev, pagination: { ...prev.pagination, page } }));
  };

  return (
    <NavbarSidebarLayout>
      <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("temporalActivity:locationAlias.title")}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("temporalActivity:locationAlias.title")}
                </h1>
                <p className="text-gray-500">{t("temporalActivity:locationAlias.manage")}</p>
              </div>
              {isCompanyAdminUser(user) && (
                <Button color="primary" onClick={() => setIsCreateModalOpen(true)}>
                  <HiPlus className="mr-2 h-5 w-5" />
                  {t("temporalActivity:locationAlias.create")}
                </Button>
              )}
            </div>
          </div>

          <LocationAliasListTable
            data={aliasesData?.items || []}
            pagination={
              aliasesData?.pagination
                ? {
                    pageSize: aliasesData.pagination.pageSize || 15,
                    pageIndex: aliasesData.pagination.pageIndex ?? aliasesData.pagination.page ?? 1,
                    totalPages: aliasesData.pagination.totalPages ?? 1,
                    totalItems: aliasesData.pagination.totalItems ?? aliasesData.pagination.total ?? 0,
                  }
                : { pageSize: 15, pageIndex: 1, totalPages: 1, totalItems: 0 }
            }
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      <LocationAliasFormDialog
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
      />
    </NavbarSidebarLayout>
  );
}
